import { Entry, ReferencedEntry } from 'contentful';
import { parse } from 'qs';

import { Product, SalesChannel, ShopContentType } from 'src/api/contentful/contentTypes.shop';
import { fetchEntries } from 'src/api/contentful/fetchEntries';
import { cookieNames } from 'src/constants/cookieNames';
import { readCookie, setCookie } from 'src/utils/Cookies';
import { get, getField, getSys, isEntry } from 'src/utils/EntryUtil';
import { Environment } from 'src/utils/Environment';
import { LocalStorage } from 'src/utils/LocalStorage';

export const REFERRAL_ID_COOKIE_NAME = 'referralId';
export const TRACKING_HOST_KEY = 'store.trackingHost';
export const SALES_CHANNEL_SYS_ID_KEY = 'salesChannelSysId';

export async function fetchSalesChannel(locale: string, referrerDomains: string, api = Environment.current.shopContentApi) {
  const salesChannels = await fetchEntries<SalesChannel>(locale, {
    contentType: ShopContentType.SALES_CHANNEL,
    filters: { fields: { referrerDomains } },
    include: 2,
    limit: 1,
  }, api);

  const [ item ] = get(salesChannels, 'items', []) as Array<ReferencedEntry<SalesChannel>>;

  return item;
}

export function getPartnerKey(): string | undefined {

  // this works for the initial renders but then doesn't work once the URL changes
  const basepath = window.location.pathname.split('/')[1];
  if (basepath && basepath !== 'shop' && basepath === 'partner') {
      return window.location.pathname.split('/')[2];
  }

  const params = parse(document.location.search, { ignoreQueryPrefix: true });
  const value = params.partner || params.trackingHost;
  const param = Array.isArray(value) ? value[0] : value;

  if (typeof param === 'string') return param;

  const localStorageTrackingHost = LocalStorage.tryGetItem(TRACKING_HOST_KEY);

  if (localStorageTrackingHost) return localStorageTrackingHost;

  if (params.utm_campaign) {
    return Environment.current.isDev ? 'clover-test' : 'clover.com';
  }

  return undefined;
}

export function setStorageData(salesChannel?: ReferencedEntry<SalesChannel>) {
  if (!isEntry(salesChannel)) return;

  const sysId = getSys(salesChannel, 'id');
  if (sysId) {
    LocalStorage.trySetItem(SALES_CHANNEL_SYS_ID_KEY, sysId);
  }

  const referralId = getField(salesChannel, 'referralId', '');
  setCookie(REFERRAL_ID_COOKIE_NAME, referralId);

  const [ partnerKey ] = getField(salesChannel, 'referrerDomains', []);
  if (partnerKey) {
    LocalStorage.trySetItem(TRACKING_HOST_KEY, partnerKey);
  }
}

export function getReferrerPhone() {
  return readCookie(cookieNames.REFERRER_PHONE);
}

export function getReferrer() {
  return readCookie(cookieNames.REFERRER);
}

export function getReferralType() {
  return readCookie(cookieNames.REFERRAL_TYPE);
}

export function extractUniqueProductIds(products?: Array<ReferencedEntry<Product>>) {
  if (!products) return [];

  const productIds = products.map((product: Entry<Product>) =>
    getField(product, 'firstDataMarketplaceId')
  );

  const nonEmptyProductIds = new Set(productIds.filter(notEmpty));
  return Array.from(nonEmptyProductIds);
}

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}
